import type { MechanicDefinition } from "./types";

export const survival: MechanicDefinition = {
  id: "survival",
  disabled: true,
  name: "Survival",
  description: "Hunger, thirst, rest, and environmental hazards",
  icon: "Flame",
  category: "systems",
  auditPasses: 1,
  aiHints: {
    purpose: "Tracks hunger, thirst, rest needs and environmental exposure as gameplay constraints.",
    whenToUse: "Survival-focused worlds, wilderness adventures, post-apocalyptic, or harsh environment games.",
    synergies: ["time_passage", "inventory", "resting", "region_movement"],
    conflicts: [],
    prerequisites: [],
    tags: ["survival", "hunger", "thirst", "rest", "hazards", "wilderness"],
    complexity: 2,
    auditPasses: 1,
    exampleConfig: { needs: ["hunger", "thirst", "rest"], depletion_rate: "moderate" },
    stateVariables: ["hunger", "thirst", "rest_need"],
  },
  configSchema: [
    {
      key: "harshness",
      label: "Harshness",
      type: "select",
      options: [
        { value: "light", label: "Light (reminders, mild penalties)" },
        { value: "moderate", label: "Moderate (real consequences)" },
        { value: "hardcore", label: "Hardcore (constant threat)" },
      ],
      default: "moderate",
    },
    {
      key: "track_needs",
      label: "Tracked Needs",
      type: "text",
      default: "hunger,thirst,rest",
      description: "Comma-separated needs to track",
    },
  ],
  systemPromptSection: (config) => {
    const harshness = config.harshness || "moderate";
    const needs = (config.track_needs || "hunger,thirst,rest").split(",").map((s: string) => s.trim());
    return `## SURVIVAL (Hard Mechanic)
- Harshness: ${harshness}
- Tracked needs: ${needs.join(", ")}
- Track each need as a state variable (0-100 scale, 100=fully satisfied, 0=critical).
- Use the update_needs tool to change need values. Needs deplete over time/turns.
- ${harshness === "light" ? "Gentle reminders when needs get low." : harshness === "hardcore" ? "Penalties stack quickly. At 0 the character starts taking damage or passing out." : "Moderate penalties at low levels (reduced stats, disadvantage on checks)."}
- Players must eat, drink, and rest to maintain their condition.
- Environmental hazards (extreme cold, heat, toxic air) accelerate need depletion.
- Describe physical symptoms as needs drop.

### D&D EXHAUSTION MAPPING
When survival needs drop critically low, apply exhaustion via apply_condition:
- Any need hits 0: apply exhaustion_1 (disadvantage on ability checks)
- 2+ needs at 0: apply exhaustion_2 (speed halved)
- All needs at 0 for 2+ turns: apply exhaustion_3 (disadvantage on attacks/saves)
- All needs at 0 for 5+ turns: apply exhaustion_4 (HP max halved)
- All needs at 0 for 8+ turns: apply exhaustion_5 (speed 0)
- All needs at 0 for 10+ turns: apply exhaustion_6 (death)
- Long rest reduces exhaustion by 1 level. Eating/drinking reduces need-based exhaustion when needs rise above thresholds.`;
  },
  widgets: [
    {
      id: "survival_status",
      type: "status_bar_item",
      label: "Vitals",
      icon: "Flame",
      component: "SurvivalWidget",
      dataBindings: {
        hunger: "variables.need_hunger",
        thirst: "variables.need_thirst",
        rest: "variables.need_rest",
      },
      visibility: "when_active",
      priority: 5,
    },
    {
      id: "survival_tab",
      type: "sidebar_tab",
      label: "Survival",
      icon: "Flame",
      component: "SurvivalWidget",
      dataBindings: {
        hunger: "variables.need_hunger",
        thirst: "variables.need_thirst",
        rest: "variables.need_rest",
      },
      visibility: "when_data",
      priority: 55,
    },
  ],
  initializeState: (config) => {
    const needs = (config.track_needs || "hunger,thirst,rest").split(",").map((s: string) => s.trim());
    const defaults: Record<string, any> = {};
    for (const need of needs) {
      defaults[`need_${need}`] = 80; // start mostly satisfied
    }
    return defaults;
  },
  // React to combat hits (drains rest), spell casts (drains rest)
  onEvent: (event) => {
    if (event.type === "combat_hit" && event.data.is_player_hit) {
      return {
        stateUpdates: { _need_rest_delta: -5 },
        narrativeNotes: ["🫠 Combat exertion: rest -5"],
      };
    }
    if (event.type === "spell_cast") {
      return {
        stateUpdates: { _need_rest_delta: -3 },
        narrativeNotes: ["✨ Spell exertion: rest -3"],
      };
    }
    return null;
  },
  // Deplete needs each turn based on harshness
  postTurnHook: (context) => {
    const harshness = context.config.harshness || "moderate";
    const depletionRate: Record<string, number> = { light: 2, moderate: 5, hardcore: 8 };
    const rate = depletionRate[harshness] || 5;
    const needs = (context.config.track_needs || "hunger,thirst,rest").split(",").map((s: string) => s.trim());
    const updates: Record<string, any> = {};
    const notes: string[] = [];
    for (const need of needs) {
      const key = `need_${need}`;
      const current = context.state.variables?.[key] ?? 80;
      // Also apply any pending delta from onEvent
      const delta = context.state.variables?.[`_${key}_delta`] || 0;
      const newVal = Math.max(0, current - rate + delta);
      updates[key] = newVal;
      if (delta) updates[`_${key}_delta`] = 0; // clear pending
      if (newVal <= 20) notes.push(`⚠ ${need} critically low (${newVal}/100)`);
    }
    return { stateUpdates: updates, narrativeNotes: notes.length > 0 ? notes : undefined };
  },
  tools: [
    {
      type: "function",
      function: {
        name: "update_needs",
        description: "Update a survival need (hunger, thirst, rest, etc.) for the player",
        parameters: {
          type: "object",
          properties: {
            need: { type: "string", description: "Which need to update (hunger, thirst, rest, etc.)" },
            change: { type: "number", description: "Amount to change (-100 to +100). Negative = depletion, positive = restoration" },
            reason: { type: "string", description: "What caused the change (eating, resting, environmental hazard, etc.)" },
          },
          required: ["need", "change", "reason"],
        },
      },
    },
  ],
};
